<template>
  <li class="game-condition-item">
    <dropdown-component
      class="game-story-dropdown"
      @change="handleEventChange"
      :value="awardSelection"
      :invalidMessage="validationErrors.gameAward"
      :disabled="disableFields"
    >
      <template #internal-caption v-if="awardSelectedObject">
        <span class="icon">
          <GameStory32 v-if="isGameStoryIcon(awardSelectedObject.type)" />
          <Achievement32 v-if="isAchievementIcon(awardSelectedObject.type)" />
          <GameAvatar32 v-if="isGameAvatarIcon(awardSelectedObject.type)" />
          <DigitalBadge32 v-if="isDigitalBadgeIcon(awardSelectedObject.type)" />
        </span>

        <ellipsis-text :text="awardSelectedObject.title" />
      </template>

      <cv-dropdown-item
        v-for="gameAward in processGameAwards"
        :key="`${gameAward.id}${gameAward.title}`"
        :value="gameAward.id.toString()"
      >
        <span class="icon">
          <GameStory32 v-if="isGameStoryIcon(gameAward.type)" />
          <Achievement32 v-if="isAchievementIcon(gameAward.type)" />
          <GameAvatar32 v-if="isGameAvatarIcon(gameAward.type)" />
          <DigitalBadge32 v-if="isDigitalBadgeIcon(gameAward.type)" />
        </span>

        <ellipsis-text :text="gameAward.title" />
      </cv-dropdown-item>
    </dropdown-component>
    <span>{{ t("gamification_game_condition_achievement_awarded_label") }}</span>
    <cv-button kind="ghost" class="icon-button btn-delete-condition">
      <svg-icon name="Trash24" kind="action" @click="deleteCondition" />
    </cv-button>
  </li>
</template>

<script setup lang="ts">
import { GAME_AWARD_TYPE } from "@modules/attempt/service/attempt.constant";
import GameStory32 from "@images/icons/indicative/32px/GameStory32.svg?component";
import Achievement32 from "@images/icons/indicative/32px/Achievement32.svg?component";
import GameAvatar32 from "@images/icons/indicative/32px/GameAvatar32.svg?component";
import DigitalBadge32 from "@images/icons/indicative/32px/DigitalBadge32.svg?component";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const emit = defineEmits(["change", "delete"]);

// Props
interface Props {
  condition: object;
  targetGameAward?: object;
  gameAwards?: unknown[]; // Replace `any` with your game award type
  disableFields?: boolean;
  validationErrors?: object;
}

const {
  condition,
  targetGameAward,
  gameAwards = [],
  disableFields = false,
  validationErrors = {},
} = defineProps<Props>();

// Data
const gameAwardSelected = ref(condition.gameAwardPrerequisite?.toString());

// Computed
const awardSelection = computed(() => {
  if (processGameAwards.value.some((award) => award.id.toString() === gameAwardSelected.value)) {
    return gameAwardSelected?.value.toString() ?? null;
  }
  return null;
});

const processGameAwards = computed(() => {
  return targetGameAward
    ? gameAwards.filter((award) => award.id !== targetGameAward.id)
    : gameAwards;
});

const awardSelectedObject = computed(() => {
  return processGameAwards.value.find(
    (item) => item.id.toString() === awardSelection.value?.toString(),
  );
});

// Methods
const isGameStoryIcon = function (gameAwardType) {
  return gameAwardType === GAME_AWARD_TYPE.GAME_STORY;
};

const isAchievementIcon = function (gameAwardType) {
  return gameAwardType === GAME_AWARD_TYPE.ACHIEVEMENT;
};

const isGameAvatarIcon = function (gameAwardType) {
  return gameAwardType === GAME_AWARD_TYPE.GAME_AVATAR;
};

const isDigitalBadgeIcon = function (gameAwardType) {
  return gameAwardType === GAME_AWARD_TYPE.DIGITAL_BADGE;
};

const handleEventChange = function (id) {
  emit("change", {
    targetId: condition.id,
    id: id,
  });
};

const deleteCondition = function () {
  emit("delete", condition.id);
};

watch(
  () => condition.gameAwardPrerequisite,
  (value) => {
    gameAwardSelected.value = value?.toString();
  },
);
</script>
